import { useState } from 'react';
import toast from 'react-hot-toast';
import { FiLock, FiSettings } from 'react-icons/fi';

import { batchGenerationsApi } from '@/services/batchGenerations';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SelectNative } from '@/components/ui/select-native';

// Modelos disponíveis no backend de geração (item_generator_openai.py).
// Custo aproximado p/ batch completo (56 descritores × 1 item) — apenas
// referência para o operador. Backend usa o nome literal.
const MODELS = [
  { value: 'gpt-5.5-2026-04-23', label: 'gpt-5.5 (recomendado) — high reasoning ~$3,80', effortDefault: 'high' },
  { value: 'gpt-5', label: 'gpt-5 — reasoning intenso ~$3,64', effortDefault: 'high' },
  { value: 'gpt-5-mini', label: 'gpt-5-mini — mais barato ~$0,36', effortDefault: 'medium' },
  { value: 'gpt-5-nano', label: 'gpt-5-nano — exploratório ~$0,07', effortDefault: 'low' },
  { value: 'gpt-4.1', label: 'gpt-4.1 — sem reasoning, rápido ~$2,92', effortDefault: null },
  { value: 'gpt-4.1-mini', label: 'gpt-4.1-mini — rápido e barato ~$0,58', effortDefault: null },
  { value: 'o3', label: 'o3 — reasoning STEM ~$2,92', effortDefault: 'high' },
  { value: 'o4-mini', label: 'o4-mini — STEM mais leve ~$1,60', effortDefault: 'medium' }
];

const EFFORTS = ['minimal', 'low', 'medium', 'high'];

const PROMPT_VERSIONS = [
  { value: 'v0.2.0-openai-anticlang', label: 'v0.2.0 — anti-clang (recomendado)' },
  { value: 'v0.1.0-openai', label: 'v0.1.0 — original' }
];

// Etapas disponíveis para geração. Mantém sincronia com Question::ETAPAS no backend.
// `descriptorsCount` é referência para o operador (matrix carrega no backend).
const ETAPAS = [
  { value: 'serie_3_em', label: '3ª série EM (SAEB 2001-2023)', descriptorsCount: 56, enabled: true, descriptorsHint: 'LP: D1–D21 (21). MAT: D1–D35 (35).' },
  { value: 'ano_9_ef', label: '9º ano EF (BNCC)', descriptorsCount: 86, enabled: true, descriptorsHint: 'LP: 9L1.*/9L2.*/9L3.*/9L4.*, 9AL2.*/9AL3.*, 9P4.* (27). MAT: 9N*, 9A*, 9G*, 9M*, 9E* (59).' },
  { value: 'ano_5_ef', label: '5º ano EF (BNCC)', descriptorsCount: 83, enabled: true, descriptorsHint: 'LP: 5L1.*/5L2.*/5L3.*, 5AL1.*/5AL2.*/5AL3.*, 5P4.* (27). MAT: 5N*, 5A*, 5G*, 5M*, 5E* (56).' },
  { value: 'ano_2_ef', label: '2º ano EF (BNCC)', descriptorsCount: 43, enabled: true, descriptorsHint: 'LP: 2L1.*/2L2.*, 2AL1.*, 2P4.* (10). MAT: 2N*, 2A*, 2G*, 2M*, 2E* (33). Foco em alfabetização e numeralização inicial.' }
];

const STEP = { PASSWORD: 1, CONFIG: 2 };

export default function GenerateBatchModal({ open, onOpenChange, onSuccess }) {
  const [step, setStep] = useState(STEP.PASSWORD);
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Programa: 'saeb' (padrão) ou 'enem'
  const [program, setProgram] = useState('saeb');

  // Config padrão (sweet spot pedagógico)
  const [etapa, setEtapa] = useState('serie_3_em');
  const [model, setModel] = useState('gpt-5.5-2026-04-23');
  const [promptVersion, setPromptVersion] = useState('v0.2.0-openai-anticlang');
  const [reasoningEffort, setReasoningEffort] = useState('high');
  const [perDescriptor, setPerDescriptor] = useState(1);
  const [alternativesCount, setAlternativesCount] = useState(5);
  const [descriptorCodes, setDescriptorCodes] = useState(''); // CSV; vazio = todos
  const etapaInfo = ETAPAS.find(e => e.value === etapa);

  const isReasoningModel = model.startsWith('gpt-5') || model.startsWith('o');

  const handleClose = () => {
    if (submitting) return;
    setStep(STEP.PASSWORD);
    setPassword('');
    onOpenChange(false);
  };

  const handleNext = e => {
    e.preventDefault();
    if (!password.trim()) {
      toast.error('Informe sua senha para continuar.');
      return;
    }
    setStep(STEP.CONFIG);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const codes = descriptorCodes
        .split(',')
        .map(s => s.trim().toUpperCase())
        .filter(Boolean);

      const json = await batchGenerationsApi.create({
        password,
        program,
        etapa: program === 'enem' ? null : etapa,
        generator_model: model,
        prompt_version: promptVersion,
        reasoning_effort: isReasoningModel ? reasoningEffort : null,
        descriptor_codes: codes,
        per_descriptor: Number(perDescriptor) || 1,
        alternatives_count: program === 'enem' ? 5 : alternativesCount
      });
      toast.success('Pedido de geração registrado — acompanhe o progresso na próxima tela.');
      onSuccess?.(json?.data);
      handleClose();
    } catch (err) {
      const status = err?.response?.status;
      const apiError = err?.response?.data?.errors?.[0];
      if (status === 422 && apiError?.id === 'password') {
        toast.error(apiError?.title || 'Senha incorreta.');
        setStep(STEP.PASSWORD);
        setPassword('');
      } else {
        toast.error(apiError?.title || 'Falha ao registrar pedido.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) handleClose();
      }}
    >
      <DialogContent size="lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-primary-dark">
            {step === STEP.PASSWORD ? <FiLock /> : <FiSettings />}
            {step === STEP.PASSWORD ? 'Autorização' : 'Configurar batch'}
          </DialogTitle>
          <DialogDescription>
            {step === STEP.PASSWORD
              ? 'Informe a senha de autorização da equipe para disparar a geração via IA. O custo será debitado da conta OpenAI configurada no backend.'
              : 'Defina os parâmetros do batch. A geração roda assincronamente; você acompanha o progresso na fila de revisão depois.'}
          </DialogDescription>
        </DialogHeader>

        {step === STEP.PASSWORD ? (
          <form
            onSubmit={handleNext}
            className="space-y-3"
          >
            <div>
              <Label htmlFor="batch-password">Senha de autorização</Label>
              <Input
                id="batch-password"
                type="password"
                autoFocus
                autoComplete="off"
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••"
              />
              <p className="mt-1 text-xs text-neutral">Senha compartilhada da equipe (não é a sua senha pessoal). Não é armazenada.</p>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button type="submit">Continuar</Button>
            </DialogFooter>
          </form>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <fieldset>
              <legend className="mb-1 text-sm font-semibold text-primary">Programa</legend>
              <div className="flex gap-4">
                <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="batch-program"
                    value="saeb"
                    checked={program === 'saeb'}
                    onChange={() => setProgram('saeb')}
                  />
                  SAEB
                </label>
                <label className="flex cursor-pointer items-center gap-1.5 text-sm">
                  <input
                    type="radio"
                    name="batch-program"
                    value="enem"
                    checked={program === 'enem'}
                    onChange={() => setProgram('enem')}
                  />
                  ENEM
                </label>
              </div>
            </fieldset>

            {program === 'saeb' && (
              <div>
                <Label htmlFor="batch-etapa">Etapa</Label>
                <select
                  id="batch-etapa"
                  value={etapa}
                  onChange={e => setEtapa(e.target.value)}
                  className="w-full rounded border border-neutral-lightest px-3 py-2 text-sm"
                >
                  {ETAPAS.map(e => (
                    <option
                      key={e.value}
                      value={e.value}
                      disabled={!e.enabled}
                    >
                      {e.label}
                      {e.enabled ? ` — ${e.descriptorsCount} descritores` : ''}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-neutral">{etapaInfo?.descriptorsHint}</p>
              </div>
            )}

            <div>
              <Label htmlFor="batch-model">Modelo gerador</Label>
              <select
                id="batch-model"
                value={model}
                onChange={e => {
                  const nv = e.target.value;
                  setModel(nv);
                  const def = MODELS.find(m => m.value === nv)?.effortDefault;
                  if (def) setReasoningEffort(def);
                }}
                className="w-full rounded border border-neutral-lightest px-3 py-2 text-sm"
              >
                {MODELS.map(m => (
                  <option
                    key={m.value}
                    value={m.value}
                  >
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {isReasoningModel && (
              <div>
                <Label htmlFor="batch-effort">Reasoning effort</Label>
                <select
                  id="batch-effort"
                  value={reasoningEffort}
                  onChange={e => setReasoningEffort(e.target.value)}
                  className="w-full rounded border border-neutral-lightest px-3 py-2 text-sm"
                >
                  {EFFORTS.map(eff => (
                    <option
                      key={eff}
                      value={eff}
                    >
                      {eff}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-neutral">high produz itens melhor calibrados (custo e tempo maiores).</p>
              </div>
            )}

            <div>
              <Label htmlFor="batch-prompt-version">Versão do prompt</Label>
              <select
                id="batch-prompt-version"
                value={promptVersion}
                onChange={e => setPromptVersion(e.target.value)}
                className="w-full rounded border border-neutral-lightest px-3 py-2 text-sm"
              >
                {PROMPT_VERSIONS.map(v => (
                  <option
                    key={v.value}
                    value={v.value}
                  >
                    {v.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="batch-codes">Descritores (vazio = todos)</Label>
                <Input
                  id="batch-codes"
                  placeholder="D1, D8, D17, ..."
                  value={descriptorCodes}
                  onChange={e => setDescriptorCodes(e.target.value)}
                />
                <p className="mt-1 text-xs text-neutral">Separe por vírgula. Vazio gera para todos os {etapaInfo?.descriptorsCount}.</p>
              </div>
              <div>
                <Label htmlFor="batch-per">Itens por descritor</Label>
                <Input
                  id="batch-per"
                  type="number"
                  min={1}
                  max={10}
                  value={perDescriptor}
                  onChange={e => setPerDescriptor(e.target.value)}
                />
                <p className="mt-1 text-xs text-neutral">Padrão: 1 — varie para gerar variantes.</p>
              </div>
            </div>

            <div>
              <Label htmlFor="batch-alts">Número de alternativas</Label>
              <SelectNative
                id="batch-alts"
                value={program === 'enem' ? 5 : alternativesCount}
                onChange={e => setAlternativesCount(Number(e.target.value))}
                disabled={program === 'enem'}
                className="w-full rounded border border-neutral-lightest px-3 py-2 text-sm"
              >
                <option value={5}>5 alternativas (A–E) — padrão SAEB/ENEM</option>
                <option value={4}>4 alternativas (A–D) — útil para anos iniciais</option>
              </SelectNative>
              {program === 'enem' && <p className="mt-1 text-xs text-neutral">ENEM usa sempre 5 alternativas (A–E).</p>}
            </div>

            <p className="bg-warning/10 text-warning-dark rounded px-3 py-2 text-xs">O pedido será registrado e executado em background. Tempo estimado depende do modelo e quantidade de itens (gpt-5.5 high ≈ 2 min/item; gpt-5-mini ≈ 30 s/item).</p>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(STEP.PASSWORD)}
                disabled={submitting}
              >
                Voltar
              </Button>
              <Button
                type="submit"
                disabled={submitting}
              >
                {submitting ? 'Enviando...' : 'Gerar batch'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
